import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ShieldCheck, Globe2 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { scrollToSection } from "../lib/constants";
import { useI18n } from "../context/I18nContext";

const chapter = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const Included = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-3">
        <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.6} />
        <span className="font-sans text-navy/70 text-sm leading-relaxed">{it}</span>
      </li>
    ))}
  </ul>
);

const RateTable = ({ rows }) => (
  <div className="mt-6 divide-y divide-navy/10 border-t border-navy/10">
    {rows.map((r) => (
      <div key={r[0]} className="flex items-center justify-between py-3.5 gap-4">
        <span className="font-sans text-navy/70 text-sm">{r[0]}</span>
        <span className="font-serif text-navy text-lg whitespace-nowrap">{r[1]}</span>
      </div>
    ))}
  </div>
);

const Section = ({ n, title, subtitle, children, dark }) => (
  <motion.div
    variants={chapter}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    className={`rounded-2xl p-8 md:p-12 ${dark ? "bg-navy grain text-white" : "bg-white border border-navy/10"}`}
  >
    <div className="flex items-baseline gap-4 mb-6">
      <span className="font-serif text-3xl text-gold">{n}</span>
      <div>
        <h3 className={`font-serif text-2xl md:text-4xl ${dark ? "text-white" : "text-navy"}`}>{title}</h3>
        {subtitle && <p className={`font-sans text-sm mt-2 ${dark ? "text-white/50" : "text-navy/50"}`}>{subtitle}</p>}
      </div>
    </div>
    {children}
  </motion.div>
);

const range = (n) => Array.from({ length: n }, (_, i) => i + 1);

export default function Pricing() {
  const navigate = useNavigate();
  const { t } = useI18n();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 px-6 lg:px-10 bg-navy grain overflow-hidden">
        <div className="max-w-[1100px] mx-auto relative z-10 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
            className="font-sans text-gold text-[11px] tracking-luxury uppercase mb-6">
            {t("fees.label")}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}
            data-testid="pricing-title" className="font-serif text-white text-5xl md:text-7xl leading-none">
            {t("fees.hA")} <span className="italic text-gold">{t("fees.hB")}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-sans text-white/60 text-sm md:text-base max-w-2xl mx-auto mt-8 leading-relaxed">
            {t("fees.sub")}
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section data-testid="pricing-content" className="bg-[#f5f6f8] py-20 md:py-28 px-6 lg:px-10">
        <div className="max-w-[1100px] mx-auto">
          <button onClick={() => navigate("/")}
            className="flex items-center gap-2 font-sans text-navy/50 text-[11px] tracking-wide-lux uppercase hover:text-gold transition-colors mb-12">
            <ArrowLeft className="w-4 h-4" /> {t("fees.back")}
          </button>

          <div className="space-y-8">
            <Section n="01" title="Exclusive Listing Fee" subtitle={t("fees.s1.sub")}>
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <p className="font-sans text-navy/50 text-xs tracking-wide-lux uppercase mb-4">{t("fees.includes")}</p>
                  <Included items={range(7).map((n) => t(`fees.s1.i${n}`))} />
                </div>
                <div>
                  <p className="font-sans text-navy/50 text-xs tracking-wide-lux uppercase mb-2">{t("fees.indicative")}</p>
                  <RateTable rows={[
                    [t("fees.rowCar"), t("fees.s1.v1")],
                    [t("fees.rowYacht"), t("fees.s1.v2")],
                    [t("fees.rowJet"), t("fees.s1.v3")],
                    [t("fees.rowProperty"), t("fees.s1.v4")],
                  ]} />
                </div>
              </div>
            </Section>

            <Section n="02" title="Premium Media Production Package" subtitle={t("fees.s2.sub")} dark>
              <div className="grid md:grid-cols-2 gap-10">
                <ul className="space-y-2.5">
                  {range(7).map((n) => (
                    <li key={n} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.6} />
                      <span className="font-sans text-white/75 text-sm leading-relaxed">{t(`fees.s2.i${n}`)}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center">
                  <p className="font-sans text-white/50 text-sm leading-relaxed border-l border-gold/40 pl-6">
                    {t("fees.s2.note")}
                  </p>
                </div>
              </div>
            </Section>

            <Section n="03" title="Success Fee" subtitle={t("fees.s3.sub")}>
              <RateTable rows={[
                [t("fees.rowAuto"), "5 %"],
                [t("fees.rowYacht"), "3 – 5 %"],
                [t("fees.rowAviation"), "2 – 4 %"],
                [t("fees.rowRE"), "3 – 6 %*"],
              ]} />
              <p className="font-sans text-navy/40 text-xs mt-4">{t("fees.s3.note")}</p>
            </Section>

            <Section n="04" title={t("fees.s4.title")} subtitle={t("fees.s4.sub")}>
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <p className="font-serif text-navy text-xl mb-1">A. Travel Expenses at Actual Cost</p>
                  <p className="font-sans text-navy/50 text-xs mb-4">{t("fees.s4.aSub")}</p>
                  <div className="flex flex-wrap gap-2">
                    {range(10).map((n) => (
                      <span key={n} className="font-sans text-navy/60 text-[11px] tracking-wide-lux uppercase border border-navy/10 rounded px-3 py-1.5">{t(`fees.s4.t${n}`)}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-serif text-navy text-xl mb-1">B. International Representation Fee</p>
                  <p className="font-sans text-navy/50 text-xs mb-2">{t("fees.s4.bSub")}</p>
                  <RateTable rows={[
                    [t("fees.s4.r1"), t("fees.s4.v1")],
                    [t("fees.s4.r2"), t("fees.s4.v2")],
                    [t("fees.s4.r3"), t("fees.s4.v3")],
                  ]} />
                  <p className="font-sans text-navy/45 text-xs mt-4 leading-relaxed">
                    {t("fees.s4.note")}
                  </p>
                </div>
              </div>
            </Section>

            <Section n="05" title={t("fees.s5.title")}>
              <p className="font-sans text-navy/70 text-sm leading-relaxed">{t("fees.s5.p1")}</p>
              <p className="font-sans text-navy/50 text-sm leading-relaxed mt-4">{t("fees.s5.p2")}</p>
            </Section>

            <Section n="06" title={t("fees.s6.title")} dark>
              <p className="font-sans text-white/70 text-sm leading-relaxed">{t("fees.s6.p")}</p>
            </Section>

            <Section n="07" title={t("fees.s7.title")} subtitle={t("fees.s7.sub")}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {range(4).map((n) => (
                  <div key={n} className="border border-navy/10 rounded-xl p-5">
                    <ShieldCheck className="w-5 h-5 text-gold mb-3" strokeWidth={1.3} />
                    <p className="font-sans text-navy/70 text-sm leading-relaxed">{t(`fees.s7.c${n}`)}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section n="08" title={t("fees.s8.title")}>
              <div className="flex items-start gap-4">
                <Globe2 className="w-6 h-6 text-gold shrink-0 mt-1" strokeWidth={1.2} />
                <p className="font-sans text-navy/70 text-sm leading-relaxed">{t("fees.s8.p")}</p>
              </div>
            </Section>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => { navigate("/"); setTimeout(() => scrollToSection("bespoke"), 400); }}
              data-testid="pricing-cta"
              className="bg-navy text-white px-12 py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold hover:text-navy transition-all duration-400"
            >
              {t("fees.cta")}
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
